import { ChevronIcon, CrossIcon, PlusIcon } from 'assets/icons';
import { Button, ButtonStyle } from 'common/button';
import { InvitationStatusId, NotificationType } from 'common/enums';
import { Input } from 'common/input';
import { InputSelect } from 'common/input-select';
import { Menu } from 'common/menu';
import { Modal } from 'common/modal';
import { Spinner } from 'common/spinner';
import { vaultsOrderByOptions, paginationDefault } from 'config/constants';
import { appActions, AppContext } from 'context';
import {
  classnames,
  getPermissionsByVault,
  getRoleDisplay,
  isEmailValid,
} from 'helpers/utils';
import { useClickOutside } from 'hooks/click-outside';
import { debounce } from 'lodash';
import { VaultsQueryParams } from 'models/vaultsQueryParams';
import { ApiErrorArray } from 'models/api-errors-array';
import { NotificationObject } from 'models/notificationObject';
import { VaultsController } from 'networking/controllers/vaults-controller';
import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { NoteInput } from '../note/note-input';
import styles from './modal-invite-users.module.scss';

enum GuestDuration {
  oneWeek = 'one_week',
  twoWeeks = 'two_weeks',
  oneMonth = 'one_month',
  unlimited = 'unlimited_time',
}

type ModalInviteUsersProps = {
  userType: CollaboratorType;
  handleBack?: () => void;
  handleClose?: () => void;
  handleConfirm: () => void;
  withVaultSelection?: boolean;
  preselectedVault?: SelectType;
};

type User = {
  name: string;
  email: string;
};

type UserInvite = { key: string } & User;

const getUniqueKey = () => Math.random().toString(36).substring(2, 10);

const isEmailAlreadyAdded = (
  allUsers: { name: string; email: string }[],
  currentEmail: string,
) => allUsers.find((user) => user.email === currentEmail);

const ModalInviteUsers: React.FC<ModalInviteUsersProps> = ({
  userType,
  handleBack,
  handleConfirm,
  handleClose,
  withVaultSelection,
  preselectedVault,
}) => {
  const vaultsSelectRef = useRef<HTMLInputElement>(null);
  const {
    dispatch,
    state: { currentVault, subscriptionsScheme, data },
  } = useContext(AppContext);
  const [vaults, setVaults] = useState<VaultType[]>([]);
  const [selectedVault, setSelectedVault] = useState<SelectType | undefined>(
    preselectedVault,
  );
  const [search, setSearch] = useState('');
  const [queryParams, setQueryParams] = useState<VaultsQueryParams>({
    pageSize: 6,
    pageNum: 1,
    sortBy: vaultsOrderByOptions.find(
      (elem: SelectType) => elem.id === paginationDefault.lastUpdate.id,
    )!.id,
  });
  const [showGuestDurationOptions, setShowGuestDurationOptions] =
    useState(false);
  const [selectedDuration, setSelectedDuration] = useState<GuestDuration>(
    GuestDuration.unlimited,
  );

  const [note, setNote] = useState('');
  const maxChars = 300;

  const durationOptionsRef = useRef<HTMLDivElement>(null);
  useClickOutside(durationOptionsRef, () => setShowGuestDurationOptions(false));

  const [users, setUsers] = useState<UserInvite[]>([
    {
      key: getUniqueKey(),
      name: '',
      email: '',
    },
  ]);
  const [errors, setErrors] = useState<User[]>([
    {
      name: '',
      email: '',
    },
  ]);
  const [loading, setLoading] = useState(false);

  const vault = vaults.find((a) => a.id === selectedVault?.id) ?? currentVault;

  const invitesLeft =
    subscriptionsScheme && vault
      ? (getPermissionsByVault(subscriptionsScheme, vault)?.contributors || 0) -
        vault.contributors.filter(
          (c) =>
            c.invitationStatus === InvitationStatusId.accepted ||
            c.invitationStatus === InvitationStatusId.pending,
        ).length
      : 0;

  const currentInvitesLeft =
    invitesLeft -
    users.filter((user) => user.email.length && user.name.length).length;

  const fetchVaults = async () => {
    try {
      const { data: vaultsData } =
        await VaultsController.getVaults(queryParams);
      const preselectedVaultData = preselectedVault
        ? vaultsData.find((a) => a.id === preselectedVault.id) ||
          (currentVault && currentVault.id === preselectedVault.id
            ? currentVault
            : null)
        : null;
      if (
        preselectedVaultData &&
        !vaultsData.find((a) => a.id === preselectedVaultData.id)
      ) {
        const finalLenght = vaultsData.length > 5 ? 5 : vaultsData.length;
        setVaults([...vaultsData.slice(0, finalLenght), preselectedVaultData]);
      } else {
        setVaults(vaultsData);
      }
    } catch (err: any) {
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Something went wrong',
          message: 'Please try again',
          type: NotificationType.Error,
        }),
      });
    }
  };

  const handleDebounceFn = (value: string) => {
    const params = { ...queryParams };
    if (value === '') delete params.search;
    else params.search = value;
    setQueryParams({ ...params, pageNum: 1 });
  };

  const debounceFn = useCallback(debounce(handleDebounceFn, 500), [
    queryParams,
  ]);

  const onChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearch(value);
    debounceFn(value);
  };

  const updateUser = (index: number, field: keyof User, value: string) => {
    const newUsersState = users.slice();
    newUsersState[index][field] = value;
    const newErrorState = errors.slice();
    newErrorState[index][field] = '';
    setUsers(newUsersState);
    setErrors(newErrorState);
  };

  const removeUser = (index: number) => {
    setUsers((prevState) => prevState.filter((_, i) => i !== index));
    setErrors((prevState) => prevState.filter((_, i) => i !== index));
  };

  const addUser = () => {
    setUsers((prevState) => [
      ...prevState,
      {
        key: getUniqueKey(),
        name: '',
        email: '',
      },
    ]);
    setErrors((prevState) => [
      ...prevState,
      {
        name: '',
        email: '',
      },
    ]);
  };

  const getEmailError = (
    userList: { name: string; email: string }[],
    contributors: ContributorType[],
    currentEmail: string,
  ) => {
    let emailError = '';

    if (!isEmailValid(currentEmail)) {
      emailError = 'invalid email';
    } else if (isEmailAlreadyAdded(contributors, currentEmail)) {
      emailError = 'Editor has already been invited';
    } else if (isEmailAlreadyAdded(userList, currentEmail)) {
      emailError = 'Email is already on the list';
    }

    return emailError;
  };

  const validateField = (index: number, field: keyof User) => {
    let emailError = '';
    const user = users[index];
    const newErrorsState = errors.slice();
    if (user[field].trim() === '') {
      newErrorsState[index][field] = `${field} cannot be empty`;
      setErrors(newErrorsState);
    } else if (field === 'email') {
      setErrors(newErrorsState);
      if ([data.user.email, vault?.owner.email].includes(user.email)) {
        const errorMessage =
          user.email === data.user.email
            ? `Can't invite yourself`
            : `Can't invite the owner`;
        newErrorsState[index][field] = errorMessage;
        setErrors(newErrorsState);
      } else {
        const usersAdded = users.slice();
        usersAdded.splice(index, 1);
        emailError = getEmailError(
          usersAdded,
          vault?.contributors.filter(
            (c) =>
              c.invitationStatus === InvitationStatusId.accepted ||
              c.invitationStatus === InvitationStatusId.pending,
          ) ?? [],
          user[field],
        );
        newErrorsState[index][field] = emailError.toUpperCase();
        setErrors(newErrorsState);
      }
    }
  };

  const hasErrorsForm = () =>
    errors.some((error) => !!error.email || !!error.name);

  const checkAllFieldsAndGetErrors = () =>
    users.map(({ name, email }) => {
      const errs = { name: '', email: '' };

      if (name === '') {
        errs.name = 'Name cannot be empty';
      }
      if (email === '') {
        errs.email = 'Email cannot be empty';
      }
      if (!isEmailValid(email)) {
        errs.email = 'Invalid email';
      }

      return errs;
    });

  const inviteUsers = async () => {
    const newErrorsList = checkAllFieldsAndGetErrors();
    setErrors(newErrorsList);

    if (newErrorsList.some((error) => !!error.email || !!error.name)) {
      return;
    }

    setLoading(true);

    try {
      if (!vault) return;
      const body = users.map((user) => ({
        name: user.name,
        email: user.email,
      }));
      if (userType === 'guest') {
        const expiration =
          selectedDuration === GuestDuration.unlimited
            ? 'unlimited'
            : selectedDuration;
        await VaultsController.inviteGuestsToRecord({
          vaultId: vault.id,
          guests: body,
          expiration,
          note,
        });
      } else {
        await VaultsController.addContributors(vault.id, body, note);
      }
      handleConfirm();
      setLoading(false);
    } catch (error) {
      const errorMessage =
        error instanceof ApiErrorArray
          ? error.errors[0].details
          : 'Please try again later.';
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: "Can't invite collaborator(s).",
          message: errorMessage,
          type: NotificationType.Error,
        }),
      });
      setLoading(false);
    }
  };

  const sendButtonDisabled = !vault || hasErrorsForm();

  const addUserDisabled =
    (userType === 'contributor' && invitesLeft - users.length === 0) || !vault;

  const vaultOptions = vaults.map((a) => ({
    id: a.id,
    value: a.title,
  }));

  const onSelectVault = (option: SelectType) => {
    if (selectedVault?.id !== option.id) {
      setSelectedVault(option);
      setSearch(option.value);

      setUsers([
        {
          key: getUniqueKey(),
          name: '',
          email: '',
        },
      ]);
      setErrors([
        {
          name: '',
          email: '',
        },
      ]);
    }
  };

  const durationOptionsMenu = () => (
    <Menu className={styles.durationMenu} width={styles.durationMenuWidth}>
      {Object.values(GuestDuration).map((duration, index) => (
        <>
          <button
            type="button"
            className={
              selectedDuration === duration
                ? 'text__body__bold__medium__brand500'
                : 'text__body__regular__medium__gray700'
            }
            key={duration}
            onClick={() => {
              setSelectedDuration(duration);
              setShowGuestDurationOptions(false);
            }}
          >
            {duration.replace('_', ' ')}
          </button>
          {index < Object.values(GuestDuration).length - 1 && (
            <div className={styles.durationSeparator} />
          )}
        </>
      ))}
    </Menu>
  );

  useEffect(() => {
    fetchVaults();
  }, [queryParams]);

  useEffect(() => {
    if (vaultsSelectRef.current) {
      vaultsSelectRef.current.focus();
    }
  }, []);

  const showNotes = () => (
    <NoteInput
      note={note}
      setNote={setNote}
      maxChars={maxChars}
      placeholder="Write a note to be included in the email invitation."
    />
  );

  return (
    <Modal
      contentClassName={styles.container}
      onClose={handleClose}
      disableManualClosing
    >
      <button
        className={styles.closeButton}
        type="button"
        onClick={handleClose}
      >
        <CrossIcon />
      </button>
      <div className={styles.content}>
        <div className={styles.usersInfo}>
          <div className={styles.leftSide}>
            <div className="text__heading4__brand500">
              Invite {getRoleDisplay(userType)}s
            </div>
            {withVaultSelection && (
              <div className={styles.chooseVault}>
                <span className="text__body__semi__bold__medium__gray800">
                  To vault
                </span>
                <InputSelect
                  id="vaultsSelect"
                  options={vaultOptions}
                  optionSelected={selectedVault}
                  onClickOption={onSelectVault}
                  placeholder="Type to search"
                  searchValue={search}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    onChangeSearch(e)
                  }
                  emptyMessage="No vaults found"
                  ref={vaultsSelectRef}
                />
              </div>
            )}
            {userType === 'contributor' ? (
              <p className="text__body__regular__medium__gray700">
                <span className="text__body__semi__bold__medium__brand500">
                  Editors can manage all aspects of the vault
                </span>{' '}
                except for subscription and payment details. They can view, add,
                edit, and delete content.
              </p>
            ) : (
              <p className="text__body__regular__medium__gray700">
                <span className="text__body__semi__bold__medium__brand500">
                  Contributors have limited access.
                </span>{' '}
                They can only add content (record or upload) and do not have
                permission to view, edit, or delete content.
              </p>
            )}
            {userType === 'contributor' && vault && (
              <span
                className={classnames(
                  'text__body__regular__medium__gray700',
                  styles.invitesCounter,
                )}
              >
                <span className="text__body__semi__bold__medium__brand500">
                  {currentInvitesLeft > 0 ? currentInvitesLeft : 0}
                </span>{' '}
                invite{currentInvitesLeft !== 1 && 's'} left
              </span>
            )}
          </div>
          <div className={styles.rightSide}>
            <div className={styles.users}>
              {users.map(({ name, email, key }, index) => (
                <div
                  key={key}
                  className={classnames(styles.userInputs, {
                    [styles.firstInput]: index === 0,
                  })}
                >
                  <Input
                    id="name"
                    name="name"
                    label={index === 0 ? 'Name' : ''}
                    placeholder="Name"
                    value={name}
                    type="text"
                    disabled={!vault}
                    errorMessage={errors[index].name}
                    onChange={(e: any) =>
                      updateUser(index, 'name', e.target.value)
                    }
                    onBlur={() => validateField(index, 'name')}
                    className={styles.input}
                    innerClassName={classnames(
                      'text__body__regular__medium__gray700',
                      styles.inputContent,
                    )}
                  />
                  <Input
                    id="email"
                    name="email"
                    label={index === 0 ? 'Email' : ''}
                    placeholder="Email"
                    value={email}
                    type="email"
                    disabled={!vault}
                    errorMessage={errors[index].email}
                    onChange={(e: any) =>
                      updateUser(index, 'email', e.target.value)
                    }
                    onBlur={() => validateField(index, 'email')}
                    className={classnames(styles.input)}
                    innerClassName={classnames(
                      'text__body__regular__medium__gray700',
                      styles.inputContent,
                    )}
                  />
                  <Button
                    className={classnames(styles.deleteButton, {
                      [styles.firstDeleteButton]: index === 0,
                    })}
                    buttonStyle={ButtonStyle.PrimaryStroke}
                    onClick={() => removeUser(index)}
                    disabled={users.length === 1}
                  >
                    <CrossIcon />
                  </Button>
                </div>
              ))}
            </div>
            <Button
              buttonStyle={ButtonStyle.PrimaryStroke}
              className={styles.addButton}
              onClick={addUser}
              disabled={addUserDisabled}
            >
              <PlusIcon /> Add another
            </Button>
            {showNotes()}
          </div>
        </div>
        <div
          className={classnames(styles.footer, {
            [styles.footerBorder]: userType === 'guest',
          })}
        >
          <div>
            {userType === 'guest' && (
              <>
                <div
                  className={classnames(
                    'text__body__regular__medium__gray700',
                    styles.guestDuration,
                  )}
                  ref={durationOptionsRef}
                >
                  All contributors will have access for
                  <Button
                    buttonStyle={ButtonStyle.PrimaryGhost}
                    className={styles.guestDurationButton}
                    onClick={() => setShowGuestDurationOptions((prev) => !prev)}
                  >
                    {selectedDuration.replace('_', ' ')}
                    <ChevronIcon />
                  </Button>
                  {showGuestDurationOptions && durationOptionsMenu()}
                </div>
                <div className="text__body__regular__small__gray700">
                  You can always change the duration in the Collaborators
                  screen.
                </div>
              </>
            )}
          </div>
          <div className={styles.buttons}>
            <Button
              buttonStyle={ButtonStyle.GreyGhost}
              onClick={handleBack ?? handleClose}
            >
              {handleBack ? 'Back' : 'Cancel'}
            </Button>
            {loading ? (
              <Spinner className={styles.spinner} />
            ) : (
              <Button
                buttonStyle={ButtonStyle.PrimaryFilled}
                disabled={sendButtonDisabled}
                onClick={inviteUsers}
              >
                Send invite(s)
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export { ModalInviteUsers };
